import { useCallback, useEffect, useRef, useState } from 'react'
import { refreshAccount } from '../lib/account'
import { accountStatus } from '../lib/approval'
import { useT } from '../lib/i18n'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { useSearchStore } from '../stores/searchStore'
import { AlertTriangleIcon } from '../components/Icons'
import { Spinner } from '../components/Spinner'

/**
 * The waiting screen (docs/CONTRACT.md §11).
 *
 * An account that is not `approved` may call exactly one route — `GET /api/me` — so that is the
 * only call this screen makes, and it is what makes the app enter on its own the moment an admin
 * approves: a slow poll plus a check on window focus, no reload and no alert from the user.
 *
 * `pending` is a queue; `rejected` and `suspended` are refusals. Same screen, different sentence —
 * the difference comes from the `status` the api returns, not from a guess about elapsed time.
 */
const POLL_MS = 15_000

export function WaitingApprovalPage() {
  const t = useT()
  const user = useAuthStore((state) => state.user)
  const status = accountStatus(user)
  const [unreachable, setUnreachable] = useState(false)
  const checking = useRef(false)

  const check = useCallback(async () => {
    if (checking.current) return
    checking.current = true
    try {
      // The refresh itself ends the session on a revoked token, and stores the fresh status, which
      // is what flips this screen into the app — nothing else in here has to decide that.
      setUnreachable(!(await refreshAccount()))
    } finally {
      checking.current = false
    }
  }, [])

  useEffect(() => {
    void check()
    const timer = setInterval(() => void check(), POLL_MS)
    const onFocus = () => void check()
    const onVisibility = () => {
      if (!document.hidden) void check()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [check])

  const waiting = status === 'pending'

  return (
    <div className="app-shell items-center justify-center gap-3 overflow-y-auto px-6 text-center">
      <span
        aria-hidden="true"
        className="flex size-14 items-center justify-center rounded-full bg-accent/12 ring-1 ring-accent/30 ring-inset"
      >
        {waiting ? (
          <Spinner size={22} className="text-accent" label={t('approval.waiting')} />
        ) : (
          <AlertTriangleIcon size={24} className="text-warn" />
        )}
      </span>

      <h1 className="text-title font-semibold text-ink">{waiting ? t('approval.waiting') : t('approval.notApproved')}</h1>
      <p className="max-w-[26rem] text-ui text-muted">
        {waiting
          ? t('approval.pendingBody')
          : status === 'suspended'
            ? t('approval.suspendedBody')
            : t('approval.rejectedBody')}
      </p>

      {user && (
        <p className="text-meta text-muted">
          {t('approval.signedInAs')} <bdi className="text-ink">{user.displayName}</bdi>
          {user.handle ? ` (@${user.handle})` : ''}
        </p>
      )}

      {unreachable && (
        <p role="status" className="text-meta text-warn">
          {t('approval.unreachable')}
        </p>
      )}

      <button
        type="button"
        onClick={() => {
          // Deliberately local: a blocked account may call nothing but `GET /api/me`, so this
          // drops the session on this device and hands back to the front door instead of signing
          // out over the network.
          useAuthStore.getState().clearSession()
          useChatStore.getState().reset()
          useSearchStore.getState().clear()
        }}
        className="mt-1 inline-flex min-h-11 items-center gap-2 rounded-full bg-surface2 px-5 text-ui font-medium text-ink ring-1 ring-line ring-inset press transition-colors duration-quick hover:bg-surface3"
      >
        {t('approval.useDifferentAccount')}
      </button>
    </div>
  )
}
